import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { sprekersApi } from '../api';
import { formatShortDate } from '../dateUtils';
import type { PresentationTemplate, SpeakingEngagement } from '../types';

interface Props {
  template: PresentationTemplate | null;
  onOpenChange: (open: boolean) => void;
}

/** "Gebruiken" vanuit de bibliotheek: kies een spreekbeurt en maak daar een nieuwe presentatie uit het sjabloon. */
export default function UseTemplateDialog({ template, onOpenChange }: Props) {
  const navigate = useNavigate();
  const [engagements, setEngagements] = useState<SpeakingEngagement[]>([]);
  const [engagementId, setEngagementId] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!template) return undefined;
    let active = true;
    setEngagementId('');
    setError(null);
    setLoading(true);
    sprekersApi
      .listEngagements()
      .then((data) => {
        if (active) setEngagements(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setError('Kon de spreekbeurten niet laden.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [template]);

  const handleUse = async () => {
    if (!template || !engagementId) return;
    setBusy(true);
    setError(null);
    try {
      await sprekersApi.createPresentationFromTemplate(engagementId, { template_id: template.id });
      toast.success('Presentatie aangemaakt');
      onOpenChange(false);
      navigate(`/sprekers/${engagementId}`);
    } catch (err: any) {
      const message = typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Presentatie aanmaken mislukt.';
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={template !== null} onOpenChange={(next: boolean) => { if (!busy) onOpenChange(next); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Sjabloon gebruiken</DialogTitle>
          <DialogDescription>
            Er wordt een nieuwe presentatie gemaakt op basis van &ldquo;{template?.name}&rdquo;. Het sjabloon zelf blijft ongewijzigd.
            Je kunt dit alleen voor een spreekbeurt van jezelf zonder bestaande presentatie.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="use-template-engagement">Spreekbeurt</Label>
          <Select value={engagementId} onValueChange={setEngagementId} disabled={loading || busy}>
            <SelectTrigger id="use-template-engagement" aria-label="Spreekbeurt"><SelectValue placeholder={loading ? 'Laden...' : 'Kies een spreekbeurt'} /></SelectTrigger>
            <SelectContent>
              {engagements.map((engagement) => (
                <SelectItem key={engagement.id} value={engagement.id}>
                  {(engagement.title || engagement.working_title || 'Zonder titel')} · {formatShortDate(engagement.start_at)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!loading && engagements.length === 0 && !error && <p className="text-sm text-slate-500">Er zijn nog geen spreekbeurten.</p>}
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button type="button" onClick={handleUse} disabled={!engagementId || busy}>
            {busy ? 'Bezig...' : 'Presentatie maken'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
